import React, { useEffect, useState } from "react";
import { useInjection } from "infrastructure/ioc/ioc.react";
import IRepository from "data/IRepository";
import { IocTypes } from "infrastructure/ioc/ioc.types";
import { Ets2_AffidavitSigned } from "data/endpoints";
import Alert from "features/shared/alerts/components/Alert";
import ActionResultDTO from "features/shared/dtos/ActionResultDTO";
import { isSuccessStatus } from "data/helpers/responseHelper";
import { useTranslationWithPrefix } from "features/shared/hooks/useTranslationWithPrefix";
import PostRequest from "data/PostRequest";
import { getRouteParams } from "features/shared/helpers/routeParams";
import { trackPromise } from "react-promise-tracker";
import { AlertType } from "features/shared/alerts/enums/alertType";

type Props = {
  ets2AffidavitId: string;
};

const Ets2AffidavitSignAcceptSection = ({ ets2AffidavitId }: Props) => {
  // translations
  const t = useTranslationWithPrefix(
    "ets2\\ets2",
    "ets2AffidavitSignAcceptSection"
  );

  // DI
  const repo = useInjection<IRepository<object>>(IocTypes.Repository);

  // state
  const [signed, setSigned] = useState(false);
  const [finished, setFinished] = useState(false);
  const [validationMessages, setValidationMessages] = useState<string[]>([]);

  // side effects
  useEffect(() => {
    const executeSign = async () => {
      const defaultError: string = t("errorMessage")
      if (!ets2AffidavitId) {
        setFinished(true);
        setValidationMessages([defaultError]);
        return;
      }
      try {
        const postRequest: PostRequest<object> = {
          url: Ets2_AffidavitSigned,
          data: {},
          routeParams: getRouteParams("ets2AffidavitId", ets2AffidavitId),
        };

        const response = await trackPromise(
          repo.post<ActionResultDTO<boolean>>(postRequest)
        );

        if (isSuccessStatus(response) && response.data?.result) {
          setSigned(true);
        } else {
          const msgs = response.data?.validationMessages
            ?.map(vm => vm.message)
            .filter(Boolean) || [];
          setValidationMessages(msgs.length > 0 ? (msgs as string[]) : [defaultError]);
        }
      } catch (e) {
        setValidationMessages([defaultError]);
      } finally {
        setFinished(true);
      }
    };

    executeSign();
  }, [ets2AffidavitId]);

  // sub-renders
  const renderResult = () => {
    if (!finished) return null;
    if (signed) {
      return (
        <Alert
          alertObj={{
            alertType: AlertType.Success,
            title: t("successTitle"),
            message: t("successMessage"),
            alertParam: { route: `ets2-affidavit/${ets2AffidavitId}` }
          }}
          className="mb-3"
          titleView
          titleClasName="w-100"
        />
      );
    }

    // error state
    const errorMessage = validationMessages.length > 0
      ? validationMessages.join("\n")
      : t("errorMessage");

    return (
      <Alert
        alertObj={{
          alertType: AlertType.Danger,
          title: t("errorTitle"),
          message: errorMessage,
        }}
        className="mb-3"
        titleView
        titleClasName="w-100"
      />
    );
  };

  // render
  return (
    <>
      {renderResult()}
    </>
  );
};

export default Ets2AffidavitSignAcceptSection;
